import {Divider, List, ListItem, ListItemButton, ListItemIcon, Tooltip, Typography, Box} from '@mui/material';
import SimpleBar from 'components/drawers/SimpleBar';
import {useAppDrawerInRedux, useBoardRedux, useSessionRedux, useSprintsRedux} from 'hooks';
import {useGetAllBoardsByWorkSpaceSlug, useUpdateUserBoardToBeSelected} from 'hooks/board';
import {Board} from 'models/board';
import {useEffect, useCallback, useMemo, memo} from 'react';
import {VscCircuitBoard} from 'react-icons/vsc';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {appRoutes} from 'routes';
import {MdOutlineWorkOutline} from 'react-icons/md';
import {MainDrawerProps} from '../MainDrawer';
import DrawerContentBoardtemSkeleton from './components/DrawerContentBoardtemSkeleton';
import NavUser from './NavUser';
import {WorkspaceDropDownMenu} from 'components/drawers/partials';
import {useSelector} from 'react-redux';
import {boardSelector} from 'store/boards/boardSlice';
import {workSpaceSelector} from 'store/workspace/workSpaceSlice';

import {GrApps} from 'react-icons/gr';
import {sessionSelector} from 'store/auth/sessionSlice';
interface DrawerContentProps extends MainDrawerProps {}

// Constants for better maintainability
const STYLES = {
    transparentBackground: 'transparent',
    active: {
        backgroundColor: '#ffffff',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',

        transition: 'all 0.2s ease-in-out',
    },
} as const;

const ROUTE_PATTERNS = {
    myWork: 'my-work',
    marketPlace: 'market-place',
} as const;

export const DrawerContent: React.FC<DrawerContentProps> = memo(() => {
    const {isDrawerOpened} = useAppDrawerInRedux();
    const {updateUserBoardToBeSelected} = useUpdateUserBoardToBeSelected();
    const {setSelectedBoardInRedux, setBoardsInRedux} = useBoardRedux();
    const {selectedWorkSpace} = useSelector(workSpaceSelector);
    const {boardsRedux} = useSelector(boardSelector);
    const {setSelectedSprintInRedux} = useSprintsRedux();

    const {getAllBoards, getAllBoardsLoading, getAllBoardsResponse} = useGetAllBoardsByWorkSpaceSlug();
    const {tenantId, boardId, workspaceId} = useParams();
    const {tenantSlug} = useSelector(sessionSelector);
    const navigate = useNavigate();
    const location = useLocation();

    // Memoize the workspace slug to prevent unnecessary re-renders
    const currentWorkspaceSlug = useMemo(() => {
        return workspaceId ?? selectedWorkSpace?.slug;
    }, [workspaceId, selectedWorkSpace?.slug]);

    // Memoize the boards fetching effect
    useEffect(() => {
        if (currentWorkspaceSlug) {
            setSelectedSprintInRedux(null);
            getAllBoards(currentWorkspaceSlug);
        }
    }, [currentWorkspaceSlug]);

    // Memoize board selection handler to prevent unnecessary re-renders of child components
    const handleBoardSelect = useCallback(
        (board: Board) => {
            setSelectedBoardInRedux(board);
            // Update user's selected board preference
            if (selectedWorkSpace?.id) {
                updateUserBoardToBeSelected({
                    variables: {
                        workspaceId: selectedWorkSpace?.id,
                        boardId: board.id,
                    },
                });
            }
            // Handle navigation for my-work section
            if (board.workspace) {
                navigate(appRoutes.workspace.boardListing(tenantSlug, board.workspace.slug, board.slug));
            }

            // Handle navigation for workspace section
            // if (tenantId && workspaceId) {
            // navigate(appRoutes.workspace.boardListing(tenantId, workspaceId, board.slug));
            // }
        },
        [
            setSelectedBoardInRedux,
            location.pathname,
            navigate,
            tenantSlug,
            tenantId,
            workspaceId,
            selectedWorkSpace?.id,
            updateUserBoardToBeSelected,
        ],
    );

    // Memoize my work navigation handler
    const handleMyWorkNavigation = useCallback(() => {
        navigate(appRoutes.myWork.myWork(tenantId));
    }, [navigate, tenantId]);

    const handleMarketPlaceNavigation = useCallback(() => {
        navigate(appRoutes.marketPalceRoutes.marketPlace(tenantId));
    }, [navigate, tenantId]);

    // Memoize active states
    const isMyWorkActive = useMemo(() => {
        return location.pathname.includes(ROUTE_PATTERNS.myWork);
    }, [location.pathname]);
    const isMMarketPlaceActive = useMemo(() => {
        return location.pathname.includes(ROUTE_PATTERNS.marketPlace);
    }, [location.pathname]);

    const isBoardActive = useCallback(
        (boardSlug: string) => {
            return boardId === boardSlug;
        },
        [boardId],
    );

    // Effect to handle board selection when boards data changes
    useEffect(() => {
        if (getAllBoardsResponse && getAllBoardsResponse.length > 0) {
            setBoardsInRedux(getAllBoardsResponse);

            // Only set selected board if there's a matching boardId in the URL
            if (boardId) {
                const selectedBoard = getAllBoardsResponse.find(board => board.slug === boardId);
                if (selectedBoard) {
                    setSelectedBoardInRedux(selectedBoard);
                }
            }
        }
    }, [getAllBoardsResponse, boardId, setBoardsInRedux, setSelectedBoardInRedux]);

    // Memoize board list to prevent unnecessary re-renders
    const boardList = useMemo(() => {
        return boardsRedux?.map(board => (
            <ListItem
                key={board.id}
                disablePadding
                sx={{
                    display: 'block',
                    backgroundColor: STYLES.transparentBackground,
                    ...(isBoardActive(board.slug) ? STYLES.active : {}),
                }}
            >
                <ListItemButton
                    onClick={() => handleBoardSelect(board)}
                    aria-label={`Select board ${board.title}`}
                    role="button"
                    sx={{
                        display: 'flex',
                        justifyContent: isDrawerOpened ? 'space-between' : 'center',
                        alignContent: 'center',
                    }}
                >
                    <Tooltip title={isDrawerOpened ? null : board.title}>
                        <ListItemIcon sx={{display: 'flex', alignItems: 'center'}}>
                            <VscCircuitBoard size={20} />
                            {isDrawerOpened && (
                                <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                                    <Typography
                                        ml={'8px'}
                                        noWrap
                                        color={isBoardActive(board.slug) ? 'black' : undefined}
                                    >
                                        {board.title}
                                    </Typography>
                                </Box>
                            )}
                        </ListItemIcon>
                    </Tooltip>
                </ListItemButton>
            </ListItem>
        ));
    }, [boardsRedux, isBoardActive, handleBoardSelect, isDrawerOpened]);

    // Error handling for failed board loading
    const hasLoadingError = useMemo(() => {
        return !getAllBoardsLoading && !getAllBoardsResponse && boardsRedux.length === 0;
    }, [getAllBoardsLoading, getAllBoardsResponse, boardsRedux.length]);

    // Better board list rendering with error handling
    const renderBoardList = useMemo(() => {
        if (getAllBoardsLoading && boardsRedux.length === 0) {
            return <DrawerContentBoardtemSkeleton />;
        }

        // if (hasLoadingError) {
        //     return (
        //         <Box p={2} textAlign="center">
        //             <Typography variant="body2" color="text.secondary">
        //                 Unable to load boards. Please try again.
        //             </Typography>
        //         </Box>
        //     );
        // }

        if (!boardsRedux || boardsRedux.length === 0) {
            return (
                <Box p={2} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                        No boards available
                    </Typography>
                </Box>
            );
        }

        return <List>{boardList}</List>;
    }, [getAllBoardsLoading, boardsRedux, hasLoadingError, boardList]);

    return (
        <>
            <SimpleBar sx={{'& .simplebar-content': {display: 'flex', flexDirection: 'column'}, overflow: 'hidden'}}>
                <ListItem
                    disablePadding
                    sx={{
                        display: 'block',
                        backgroundColor: STYLES.transparentBackground,
                        ...(isMyWorkActive ? STYLES.active : {}),
                    }}
                >
                    <ListItemButton
                        onClick={handleMyWorkNavigation}
                        aria-label="Navigate to My Work"
                        role="button"
                        sx={{
                            display: 'flex',
                            justifyContent: isDrawerOpened ? 'space-between' : 'center',
                            alignContent: 'center',
                        }}
                    >
                        <ListItemIcon>
                            <MdOutlineWorkOutline size={20} />
                            {isDrawerOpened && <Typography ml={'8px'}>My Work</Typography>}
                        </ListItemIcon>
                    </ListItemButton>
                </ListItem>

                <>
                    <WorkspaceDropDownMenu isMiniDrawer={isDrawerOpened} />
                    <Divider />
                    <Box overflow={'auto'} height="calc(100vh - 250px)">
                        {renderBoardList}
                    </Box>
                </>
            </SimpleBar>
            <Divider />

            <ListItem
                disablePadding
                sx={{
                    display: 'block',
                    backgroundColor: STYLES.transparentBackground,
                    ...(isMMarketPlaceActive ? STYLES.active : {}),
                }}
            >
                <ListItemButton
                    onClick={handleMarketPlaceNavigation}
                    aria-label="Navigate to Market Place"
                    role="button"
                    sx={{
                        display: 'flex',
                        justifyContent: isDrawerOpened ? 'space-between' : 'center',
                        alignContent: 'center',
                    }}
                >
                    <ListItemIcon>
                        <GrApps size={20} />
                        {isDrawerOpened && <Typography ml={'8px'}> Market Place</Typography>}
                        <Typography ml={'8px'}></Typography>
                    </ListItemIcon>
                </ListItemButton>
            </ListItem>
            <NavUser />
        </>
    );
});

DrawerContent.displayName = 'DrawerContent';

export default DrawerContent;
